import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from './Navigation';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../utils/config';
import './Cases.css';

interface Case {
  case_number: string;
  policy_number?: string | null;
  project_number?: string | null;
  address: string;
  date_start: string;
  date_end: string;
  status: string;
  created_at: string;
  updated_at: string;
  direction?: string;
  structure_type: string;
  total_floors: number;
  notes?: string;
}

const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'created_at', label: 'Date created' },
  { value: 'updated_at', label: 'Last updated' },
  { value: 'case_number', label: 'Case number' },
  { value: 'address', label: 'Address' },
  { value: 'status', label: 'Status' },
];

const PAGE_SIZES = [6, 12, 24, 48];

const Cases: React.FC = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(12);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    fetchCases();
  }, [page, perPage, sortBy, sortOrder]);

  const fetchCases = async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000); // 20s timeout when server is busy (e.g. analysis running)
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        page: String(page),
        per_page: String(perPage),
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      const response = await fetch(`${API_URL}/api/cases?${params}`, {
        method: 'GET',
        credentials: 'include',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });
      clearTimeout(timeoutId);

      if (response.status === 401) {
        setError('session_expired');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch cases');
      }

      const data = await response.json();
      const list = data.cases || [];
      const totalCount = data.total ?? list.length ?? 0;
      const pages = data.total_pages ?? 1;
      setCases(list);
      setTotal(totalCount);
      setTotalPages(pages);
      if (list.length === 0 && page > 1 && totalCount > 0) {
        setPage((p) => Math.max(1, p - 1));
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        setError('Request timed out. The server may be busy (e.g. analysis running). Please try again in a moment.');
      } else {
        setError(err instanceof Error ? err.message : 'An error occurred');
      }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const statusLabel = (status: string) => {
    switch (status) {
      case 'running': return 'Running';
      case 'awaiting_review': return 'Pending review';
      case 'completed': return 'Completed';
      case 'failed': return 'Failed';
      default: return 'Active';
    }
  };


  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [deletingCaseNumber, setDeletingCaseNumber] = useState<string | null>(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [openMenuCaseNumber, setOpenMenuCaseNumber] = useState<string | null>(null);

  const deleteCaseRequest = async (
    caseNumber: string,
  ): Promise<{ ok: true } | { ok: false; error: string; unauthorized?: boolean }> => {
    const response = await fetch(`${API_URL}/api/cases/${encodeURIComponent(caseNumber)}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { ...getAuthHeaders() },
    });
    const data = await response.json().catch(() => ({}));
    if (response.status === 401) {
      return { ok: false, error: 'Session expired.', unauthorized: true };
    }
    if (!response.ok) {
      return { ok: false, error: (data?.error as string) || 'Failed to delete case' };
    }
    return { ok: true };
  };

  const deleteCase = async (caseNumber: string, status?: string) => {
    const submitted = status === 'completed';
    const msg = submitted
      ? `Delete case ${caseNumber}? This removes the submitted report and all related data. This cannot be undone.`
      : `Delete case ${caseNumber}? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    setDeletingCaseNumber(caseNumber);
    setDeleteError(null);
    try {
      const result = await deleteCaseRequest(caseNumber);
      if (!result.ok) {
        if (result.unauthorized) {
          setError('session_expired');
          return;
        }
        setDeleteError(result.error);
        return;
      }
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(caseNumber);
        return next;
      });
      await fetchCases();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete case');
    } finally {
      setDeletingCaseNumber(null);
    }
  };

  const pageCaseNumbers = cases.map((c) => c.case_number);
  const allPageSelected =
    pageCaseNumbers.length > 0 && pageCaseNumbers.every((n) => selected.has(n));
  useEffect(() => {
    setOpenMenuCaseNumber(null);
  }, [page, perPage, sortBy, sortOrder]);

  useEffect(() => {
    if (openMenuCaseNumber === null) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      const id = openMenuCaseNumber;
      const wrap = document.querySelector(`[data-case-menu-for="${CSS.escape(id)}"]`);
      if (wrap && !wrap.contains(t)) {
        setOpenMenuCaseNumber(null);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenMenuCaseNumber(null);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [openMenuCaseNumber]);

  const toggleSelect = (caseNumber: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(caseNumber);
      else next.delete(caseNumber);
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageCaseNumbers.forEach((n) => next.delete(n));
      } else {
        pageCaseNumbers.forEach((n) => next.add(n));
      }
      return next;
    });
  };

  const deleteSelectedCases = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (
      !window.confirm(
        `Permanently delete ${ids.length} selected case${ids.length === 1 ? '' : 's'} and all related data (including submitted reports where applicable)? This cannot be undone.`,
      )
    ) {
      return;
    }
    setBulkDeleting(true);
    setDeleteError(null);
    const failed: { case_number: string; error: string }[] = [];
    try {
      for (const caseNumber of ids) {
        const result = await deleteCaseRequest(caseNumber);
        if (!result.ok) {
          if (result.unauthorized) {
            setError('session_expired');
            return;
          }
          failed.push({ case_number: caseNumber, error: result.error });
          continue;
        }
        setSelected((prev) => {
          const next = new Set(prev);
          next.delete(caseNumber);
          return next;
        });
      }
      if (failed.length > 0) {
        const preview = failed
          .slice(0, 3)
          .map((f) => `${f.case_number}: ${f.error}`)
          .join('; ');
        const more = failed.length > 3 ? ` (+${failed.length - 3} more)` : '';
        setDeleteError(`Some cases could not be deleted. ${preview}${more}`);
      }
      await fetchCases();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const selectionBusy = bulkDeleting || deletingCaseNumber !== null;

  const downloadReport = async (caseNumber: string) => {
    const downloadUrl = `${API_URL}/api/reports/${caseNumber}/download`;
    setDownloading(caseNumber);
    setDownloadError(null);
    try {
      const response = await fetch(downloadUrl, {
        method: 'GET',
        credentials: 'include',
        headers: { ...getAuthHeaders() },
      });
      if (response.status === 401) {
        setDownloadError('Please log in again to download.');
        return;
      }
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Download failed');
      }
      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition');
      const match = disposition?.match(/filename[*]?=(?:UTF-8'')?["']?([^"'\s]+)["']?/i);
      const filename = match?.[1] || `${caseNumber}_report.docx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="cases-page">
        <div className="loading-container">
          <div className="glass-card loading-card">
            <div className="loading-content">
              <div className="loading-logo">
                <div className="logo-container">
                  <img src="/icon.png" alt="Forensic AI Logo" className="logo" />
                </div>
                <h2 className="loading-title">Forensic AI</h2>
              </div>
              <p className="loading-text">Loading your cases...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cases-page">
        <Navigation />
        <div className="error-container">
          <div className="error-card">
            <div className="error-icon">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <h3>Error Loading Cases</h3>
            <p>
              {error === 'session_expired'
                ? 'Session expired or not recognized. Please log in again so we can load your cases.'
                : error}
            </p>
            {error === 'session_expired' ? (
              <button onClick={() => navigate('/login')} className="retry-button">
                <i className="fas fa-sign-in-alt"></i>
                Log in again
              </button>
            ) : (
              <button onClick={fetchCases} className="retry-button">
                <i className="fas fa-redo"></i>
                Try Again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cases-page">
      <Navigation />
      <div className="cases-container">
        {/* Hero/Intro Section */}
        <div className="hero-section">
          <div className="hero-content">
            <div className="hero-left">
              <h1 className="hero-title">
                Forensic Cases Management
              </h1>
            </div>
            <div className="hero-right">
              <p className="hero-description">
                Access and manage all your forensic analysis cases. Review case details, download comprehensive reports, 
                and track the progress of your investigations with our integrated case management system.
              </p>
              <div className="hero-actions">
                <button 
                  onClick={() => navigate('/analysis')}
                  className="hero-cta-btn"
                >
                  <i className="fas fa-plus"></i>
                  Create New Case
                </button>
              </div>
            </div>
          </div>
          <div className="hero-pattern"></div>
        </div>

        {/* Main Content Card */}
        <div className="cases-content-card">
          {downloadError && (
            <div
              className="alert alert-error"
              style={{
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '0.5rem',
              }}
            >
              <span>{downloadError}</span>
              <button
                type="button"
                onClick={() => setDownloadError(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.875rem',
                }}
              >
                Dismiss
              </button>
            </div>
          )}
          {deleteError && (
            <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
              {deleteError}
              <button
                type="button"
                onClick={() => setDeleteError(null)}
                style={{
                  marginLeft: '0.5rem',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.875rem',
                }}
              >
                Dismiss
              </button>
            </div>
          )}
          {cases.length === 0 && !loading ? (
            <div className="empty-state">
              <div className="empty-icon">
                <i className="fas fa-folder-open"></i>
              </div>
              <h3>{total > 0 ? 'No cases on this page' : 'No Cases Yet'}</h3>
              <p>
                {total > 0
                  ? 'Try another page or change filters.'
                  : 'Start your forensic analysis journey by creating your first case.'}
              </p>
              {total === 0 && (
                <button type="button" onClick={() => navigate('/analysis')} className="create-case-button">
                  <i className="fas fa-plus"></i>
                  Create First Case
                </button>
              )}
            </div>
          ) : (
            <>
            <div className="cases-toolbar">
              <div className="cases-sort">
                <label htmlFor="cases-sort-by">Sort by</label>
                <select
                  id="cases-sort-by"
                  value={sortBy}
                  onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
                  className="cases-sort-select"
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => { setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc')); setPage(1); }}
                  className="cases-sort-order-btn"
                  title={sortOrder === 'asc' ? 'Ascending – click for descending' : 'Descending – click for ascending'}
                  aria-label={`Sort order: ${sortOrder}`}
                >
                  <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                </button>
              </div>
              <div className="cases-per-page">
                <label htmlFor="cases-per-page">Per page</label>
                <select
                  id="cases-per-page"
                  value={perPage}
                  onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                  className="cases-per-page-select"
                >
                  {PAGE_SIZES.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              <div className="cases-toolbar-select">
                <label
                  htmlFor="cases-select-all-page"
                  className="cases-toolbar-select-label"
                  title="Select or clear all cases in this page"
                >
                  <input
                    id="cases-select-all-page"
                    type="checkbox"
                    className="cases-toolbar-select-checkbox"
                    checked={allPageSelected}
                    onChange={toggleSelectAllOnPage}
                    disabled={selectionBusy || pageCaseNumbers.length === 0}
                  />
                  Select all in page
                </label>
                {selected.size > 0 && (
                  <span className="cases-toolbar-selected-count" aria-live="polite">
                    {selected.size} selected
                  </span>
                )}
                {(selected.size > 0 || bulkDeleting) && (
                  <button
                    type="button"
                    className="cases-toolbar-delete-btn"
                    disabled={selectionBusy}
                    onClick={deleteSelectedCases}
                  >
                    <i className="fas fa-trash-alt" aria-hidden="true" />
                    {bulkDeleting ? 'Deleting…' : 'Delete'}
                  </button>
                )}
              </div>
              <div className="cases-pagination-info">
                {total > 0 && (
                  <span>
                    {((page - 1) * perPage) + 1}–{Math.min(page * perPage, total)} of {total}
                  </span>
                )}
              </div>
            </div>
            <div className="cases-grid">
              {cases.map((caseItem) => (
                <div
                  key={caseItem.case_number}
                  className={`case-card${selected.has(caseItem.case_number) ? ' case-card-selected' : ''}`}
                >
                  <div className="case-header">
                    <div className="case-header-main">
                      {selected.size > 0 && (
                        <input
                          type="checkbox"
                          className="case-select-checkbox"
                          checked={selected.has(caseItem.case_number)}
                          onChange={(e) => toggleSelect(caseItem.case_number, e.target.checked)}
                          disabled={selectionBusy}
                          aria-label={`Select case ${caseItem.case_number}`}
                        />
                      )}
                      <h3
                        title={caseItem.case_number}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/cases/${encodeURIComponent(caseItem.case_number)}`)}
                      >
                        <span className="case-header-title-text">{caseItem.case_number}</span>
                      </h3>
                    </div>
                    <div className="case-header-right">
                      <span className={`case-status-badge status-${caseItem.status || 'active'}`} title={caseItem.status || 'active'}>
                        {statusLabel(caseItem.status || 'active')}
                      </span>
                      <div
                        className="case-card-menu-wrap"
                        data-case-menu-for={caseItem.case_number}
                      >
                        <button
                          type="button"
                          className="case-card-menu-btn"
                          aria-label={`More actions for case ${caseItem.case_number}`}
                          aria-expanded={openMenuCaseNumber === caseItem.case_number}
                          aria-haspopup="menu"
                          disabled={selectionBusy}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuCaseNumber((o) =>
                              o === caseItem.case_number ? null : caseItem.case_number,
                            );
                          }}
                        >
                          <i className="fas fa-ellipsis-v" aria-hidden="true" />
                        </button>
                        {openMenuCaseNumber === caseItem.case_number && (
                          <div className="case-card-menu" role="menu" aria-label="Case actions">
                            <button
                              type="button"
                              role="menuitem"
                              className="case-card-menu-item"
                              onClick={() => {
                                setOpenMenuCaseNumber(null);
                                navigate(`/cases/${encodeURIComponent(caseItem.case_number)}`);
                              }}
                            >
                              <i className="fas fa-eye" aria-hidden="true" />
                              View details
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              className="case-card-menu-item"
                              disabled={selectionBusy}
                              onClick={() => {
                                setOpenMenuCaseNumber(null);
                                toggleSelect(caseItem.case_number, !selected.has(caseItem.case_number));
                              }}
                            >
                              <i
                                className={selected.has(caseItem.case_number) ? 'fas fa-minus-square' : 'fas fa-plus-square'}
                                aria-hidden="true"
                              />
                              {selected.has(caseItem.case_number) ? 'Deselect' : 'Select'}
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              className="case-card-menu-item"
                              disabled={
                                downloading === caseItem.case_number || caseItem.status !== 'completed'
                              }
                              onClick={() => {
                                setOpenMenuCaseNumber(null);
                                void downloadReport(caseItem.case_number);
                              }}
                            >
                              <i className="fas fa-download" aria-hidden="true" />
                              {downloading === caseItem.case_number ? 'Downloading…' : 'Download report'}
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              className="case-card-menu-item case-card-menu-item-danger"
                              disabled={selectionBusy}
                              onClick={() => {
                                setOpenMenuCaseNumber(null);
                                void deleteCase(caseItem.case_number, caseItem.status);
                              }}
                            >
                              <i className="fas fa-trash-alt" aria-hidden="true" />
                              {deletingCaseNumber === caseItem.case_number ? 'Deleting…' : 'Delete'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div
                    className="case-details"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/cases/${encodeURIComponent(caseItem.case_number)}`)}
                  >
                    <div className="detail-item">
                      <label>Policy #</label>
                      <p>{caseItem.policy_number?.trim() || '—'}</p>
                    </div>
                    <div className="detail-item">
                      <label>Project #</label>
                      <p>{caseItem.project_number?.trim() || '—'}</p>
                    </div>
                    <div className="detail-item">
                      <label>Address</label>
                      <p>{caseItem.address || 'N/A'}</p>
                    </div>
                    
                    <div className="detail-item">
                      <label>Date Created</label>
                      <p>{formatDate(caseItem.created_at)}</p>
                    </div>

                    <div className="detail-item">
                      <label>Structure</label>
                      <p className="capitalize">
                        {caseItem.structure_type} ({caseItem.total_floors} floor{caseItem.total_floors === 1 ? '' : 's'})
                      </p>
                    </div>
                  </div>

                  <div className="case-actions">
                    <button
                      type="button"
                      onClick={() => navigate(`/cases/${encodeURIComponent(caseItem.case_number)}`)}
                      className="view-case-button"
                      style={{
                        background: 'rgba(122, 107, 53, 0.12)',
                        border: '1px solid rgba(122, 107, 53, 0.25)',
                        color: '#7a6b35',
                        padding: '0.45rem 0.85rem',
                        borderRadius: '6px',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.4rem',
                      }}
                    >
                      <i className="fas fa-folder-open"></i>
                      View Details
                    </button>
                    {caseItem.status === 'awaiting_review' && (
                      <button
                        type="button"
                        onClick={() => navigate('/analysis/review', { state: { draftCaseNumber: caseItem.case_number } })}
                        className="review-button"
                      >
                        <i className="fas fa-edit"></i>
                        Review
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="cases-pagination">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="cases-pagination-btn"
                  aria-label="Previous page"
                >
                  <i className="fas fa-chevron-left"></i> Previous
                </button>
                <span className="cases-pagination-pages">
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="cases-pagination-btn"
                  aria-label="Next page"
                >
                  Next <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Cases;
